import React, { useState, useContext } from "react";
import { Link, useLocation } from "react-router-dom";

import MainHeader from "./MainHeader";
import NavLinks from "./NavLinks";
import SideDrawer from "./SideDrawer";
import Backdrop from "../UIElements/Backdrop";
import ProjectSidebar from "./ProjectSidebar";
import AdminSidebar from "../../../admin/components/AdminSidebar";
import AskCadenza from "../../../assistant/components/AskCadenza";
import { AuthContext } from "../../context/auth-context";
import useIsMobileNav from "../../hooks/useIsMobileNav";
import "./styling/MainNavigation.css";

// Routes on which the project sidebar should be hidden (e.g. immersive editors,
// cross-project pages like My Tasks that keep the selected project but aren't
// scoped to it).
const SIDEBAR_HIDDEN_ROUTES = ["/tasks"];
// Route prefixes with their own sidebar (the Help Center) or that fill the
// viewport (the heap editor, now addressed as /heap-editor/:heapId), hidden the same way.
const SIDEBAR_HIDDEN_PREFIXES = ["/help", "/heap-editor", "/heap-compare"];

const MainNavigation = (props) => {
  const [drawerIsOpen, setDrawerIsOpen] = useState(false);
  const [, setTempCondensedForDropdown] = useState(false);
  const auth = useContext(AuthContext);
  const location = useLocation();
  const isMobileNav = useIsMobileNav();
  const sidebarHidden =
    SIDEBAR_HIDDEN_ROUTES.includes(location.pathname) ||
    SIDEBAR_HIDDEN_PREFIXES.some((p) => location.pathname.startsWith(p));
  const isAdminRoute = location.pathname.startsWith("/admin");
  const showAdminNav =
    isAdminRoute && auth.isLoggedIn && auth.user?.role === "admin";
  const showProjectNav =
    !sidebarHidden && !isAdminRoute && auth.isLoggedIn && !!auth.selectedProject;

  // NOTE: sidebar-hidden routes (e.g. the heap editor) are made full-width by the
  // page's own CSS (the editor is position:fixed, left:0). We deliberately do NOT
  // mutate the global pinned state here — doing so left the sidebar unpinned after
  // leaving the editor, overlapping content on normal pages.

  const openDrawerHandler = () => {
    setDrawerIsOpen(true);
  };

  const closeDrawerHandler = () => {
    setDrawerIsOpen(false);
  };

  const handleUserDropdownToggle = (isOpen) => {
    setTempCondensedForDropdown(isOpen);
  };

  return (
    <React.Fragment>
      {drawerIsOpen && <Backdrop onClick={closeDrawerHandler} />}
      <SideDrawer show={drawerIsOpen} onClick={closeDrawerHandler}>
        <nav className="main-navigation__drawer-nav">
          <NavLinks
            isDrawer
            onUserDropdownToggle={handleUserDropdownToggle}
            /* On mobile this drawer is the ONLY way into the section nav —
               both sidebars' screen-edge tabs sat in the browser's back-gesture
               zone, so one hamburger now opens everything. The two are mutually
               exclusive: admin routes never show the project nav. */
            contextNav={
              !isMobileNav ? null : showAdminNav ? (
                <AdminSidebar embedded onNavigate={closeDrawerHandler} />
              ) : showProjectNav ? (
                <ProjectSidebar
                  embedded
                  isVisible
                  projectName={auth.selectedProject?.name}
                  onNavigate={closeDrawerHandler}
                />
              ) : null
            }
          />
        </nav>
      </SideDrawer>
      <MainHeader>
        <button
          className="main-navigation__menu-btn"
          onClick={openDrawerHandler}
        >
          <span />
          <span />
          <span />
        </button>
        <h1 className="main-navigation__title">
          <Link to="/" className="main-navigation__logo-link">
            {/* Icon and wordmark are gradient-filled via CSS (the SVG is used
                as a mask, not an <img>) so the whole lockup shares one live
                brand gradient rather than the flat colour baked into the file. */}
            <span className="main-navigation__logo" aria-hidden="true" />
            <span className="main-navigation__wordmark">
              <span className="main-navigation__logo-text">Orecasting</span>
              <span className="main-navigation__tagline">Powered by SLR</span>
            </span>
          </Link>
        </h1>
        <nav className="main-navigation__header-nav">
          <NavLinks onUserDropdownToggle={handleUserDropdownToggle} />
        </nav>
      </MainHeader>
      
      {/* Admin Sidebar — desktop only (mobile renders it inside the drawer) */}
      {showAdminNav && !isMobileNav && <AdminSidebar />}

      {/* Project Sidebar — desktop only (mobile renders it inside the drawer
          above), and hidden on admin routes or immersive editors */}
      {!sidebarHidden && !isAdminRoute && !isMobileNav && (
        <ProjectSidebar
          isVisible={auth.isLoggedIn && auth.selectedProject}
          projectName={auth.selectedProject?.name}
        />
      )}

      {/* Ask Cadenza — offered wherever the project nav is (renders nothing
          unless the project's company has turned it on) */}
      <AskCadenza visible={showProjectNav} />
    </React.Fragment>
  );
};

export default React.memo(MainNavigation);
